import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, PriceBreakdown } from './CheckoutSummaryElements';

export const CyberpunkTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-[#09090b] text-white"
      style={{
        fontFamily: config.fontFamily || 'monospace',
      }}
    >
      <div className="w-full max-w-4xl bg-black border-2 border-fuchsia-500/80 shadow-[0_0_25px_rgba(217,70,239,0.25)] rounded-xl overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Column */}
        <div className="md:col-span-5 bg-zinc-950 p-6 md:p-8 border-b-2 md:border-b-0 md:border-r-2 border-fuchsia-500/40 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-5 filter invert" />}
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 border border-cyan-400 text-cyan-400 bg-cyan-950/40">
                CYBER//TIER
              </span>
            </div>
            <div>
              <p className="text-[10px] text-fuchsia-400 uppercase tracking-widest font-mono">SYS_MODULE</p>
              <h2 className="text-3xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 to-cyan-400">
                {plan.tier}
              </h2>
              {plan.description && <p className="text-xs text-zinc-400 mt-1 font-mono">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} textColorClass="text-zinc-300 font-mono text-[11px]" checkColorClass="text-cyan-400" />
          </div>

          <div className="p-4 bg-zinc-900/90 border border-fuchsia-500/40">
            <p className="text-[10px] uppercase text-zinc-400 font-mono">PAYLOAD_TOTAL</p>
            <p className="text-3xl font-black text-fuchsia-400 font-mono">{currencySymbol}{finalTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Right Column */}
        <div className="md:col-span-7 p-6 md:p-8 bg-zinc-950/80 space-y-5">
          <div>
            <h1 className="text-xl font-black uppercase text-cyan-400 font-mono">{config.headerMain || 'TERMINAL CHECKOUT'}</h1>
            <p className="text-xs text-zinc-400 font-mono">{config.headerSub || 'Authenticate parameters to execute license.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields
              config={config}
              formData={formData}
              onInputChange={onInputChange}
              inputClassName="bg-black border-zinc-800 text-cyan-300 font-mono text-xs focus:border-fuchsia-500"
              labelClassName="text-zinc-400 font-mono text-[10px] uppercase tracking-wider"
            />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 text-xs font-black uppercase tracking-widest text-black bg-gradient-to-r from-cyan-400 to-fuchsia-500 hover:brightness-110 shadow-[0_0_15px_rgba(34,211,238,0.4)] transition-all font-mono disabled:opacity-50"
            >
              {isCapturing ? 'COMPILING AUTH...' : config.buttonText || '>> EXECUTE TRANSACTION'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
